import { config } from '../config';

export type PrintAgentAlign = 'left' | 'center' | 'right';

/** Built-in printer font: Font A is normal size, Font B is smaller. */
export type PrintAgentFont = 'A' | 'B';

export interface PrintAgentColumn {
    text: string;
    width: number;
    align: PrintAgentAlign;
    bold?: boolean;
    /** Vertical (height) scale multiplier. Defaults to `widthSize` if unset. */
    size?: number;
    /** Horizontal (width) scale multiplier. Defaults to `size` if unset. */
    widthSize?: number;
}

export interface PrintAgentLine {
    type: 'text' | 'divider' | 'qr' | 'barcode' | 'cut' | 'columns' | 'image';
    value?: string;
    align?: PrintAgentAlign;
    bold?: boolean;
    width?: number;
    columns?: PrintAgentColumn[];
    /** Vertical (height) scale multiplier. Defaults to `widthSize` if unset. */
    size?: number;
    /** Horizontal (width) scale multiplier. Defaults to `size` if unset. */
    widthSize?: number;
    /** Inverts text color: white text on a black background. */
    invert?: boolean;
    /**
     * Pads the line out to the full paper width and adds a blank padding
     * row above and below. Combine with `invert` for a full-width black bar.
     */
    fullWidth?: boolean;
}

export interface PrinterOptions {
    host: string;
    port: number;
    paperWidthMm?: number;
    lineSpacing?: number;
    font?: PrintAgentFont;
}

export class PrintAgentService {
    constructor(private readonly baseUrl: string) {}

    async status(): Promise<boolean> {
        try {
            const response = await fetch(`${this.baseUrl}/status`, {
                signal: AbortSignal.timeout(3000),
            });
            return response.ok;
        } catch {
            return false;
        }
    }

    async print(
        options: PrinterOptions,
        lines: PrintAgentLine[],
    ): Promise<void> {
        let response: Response;

        try {
            response = await fetch(`${this.baseUrl}/print`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    printerIp: options.host,
                    port: options.port,
                    paperWidth: options.paperWidthMm,
                    lineSpacing: options.lineSpacing,
                    font: options.font,
                    lines,
                }),
                signal: AbortSignal.timeout(10000),
            });
        } catch {
            throw new Error(
                'Print agent is unreachable. Make sure it is running.',
            );
        }

        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(
                (body as { error?: string }).error ?? 'Print job failed.',
            );
        }
    }
}

export const printAgentService = new PrintAgentService(
    config.PRINT_AGENT_BASE_URL,
);
